// src/stores/useAuthStore.js
import { create } from "zustand";
import { getKcFreshToken, fetchWithAuth } from "../api/fetchWithAuth";
 
// getJwtExp kept for any external callers that read store.token directly
function getJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp || 0;
  } catch {
    return 0;
  }
}
 
export const useAuthStore = create((set, get) => ({

  token: null,
  user: null,
  roles: [],
  orgId: null,
  orgName: null,       // populated via setOrgName once org endpoint is available

  // Day 9 Day 2 — DB-backed user profile from /auth/me. Distinct from
  // `user` (which is KC token claims) because the DB row carries flags
  // like is_restricted_operator that don't exist in the JWT. Loaded by
  // loadProfile() on App mount; selector below exposes the restricted
  // flag as a derived boolean (defaults to false until profile lands).
  profile: null,
  profileLoading: false,
  // True once loadProfile() has settled at least once (success OR failure).
  // Lets gated UI wait for the restricted-operator verdict instead of acting
  // on the default-false, which otherwise flashes the full modes strip for a
  // restricted user before /auth/me lands. Stays true on failure so a failed
  // /auth/me falls back to the non-restricted shell rather than hiding forever.
  profileLoaded: false,

  setAuth: ({ token, user, roles = [], orgId = null }) =>
    set({ token, user, roles, orgId }),

  setOrgName: (name) => set({ orgName: name }),

  clearAuth: () =>
    set({ token: null, user: null, roles: [], orgId: null, orgName: null, profile: null, profileLoaded: false }),

  /**
   * Fetch the bearer's DB-backed user row via /auth/me. Idempotent — safe
   * to call repeatedly; concurrent calls debounce via profileLoading.
   * On failure, leaves profile null (selectors default to false).
   */
  loadProfile: async () => {
    if (get().profileLoading) return;
    set({ profileLoading: true });
    try {
      const res = await fetchWithAuth("/api/v1/auth/me");
      if (!res.ok) {
        set({ profileLoading: false, profileLoaded: true });
        return;
      }
      const data = await res.json();
      set({ profile: data?.user ?? null, profileLoading: false, profileLoaded: true });
    } catch (err) {
      console.warn("[useAuthStore] loadProfile failed", err);
      set({ profileLoading: false, profileLoaded: true });
    }
  },
 
  // ensureFreshToken no longer calls kc.updateToken() directly.
  // It delegates to getKcFreshToken() — the single shared refresh coordinator
  // in fetchWithAuth.js — so there is exactly one in-flight Keycloak refresh
  // at any time, shared by both tile token fetches and regular API calls.
  // This eliminates the 30-second stale-token window and the concurrent
  // updateToken() race that was causing one system to overwrite the other's token.
  ensureFreshToken: async () => {
    const { token } = get();
    if (!token) return null;
 
    try {
      return await getKcFreshToken();
    } catch {
      return null;
    }
  },
 
  // Utility for components that only need to read expiry without triggering refresh
  getTokenExp: () => {
    const { token } = get();
    return token ? getJwtExp(token) : 0;
  },
}));

// Day 9 Day 2 — derived selector for the restricted-operator shell pattern.
// Components/route guards consume via `useAuthStore(selectIsRestrictedOperator)`
// to get a boolean that defaults to false until /auth/me lands.
export const selectIsRestrictedOperator = (s) =>
  s.profile?.is_restricted_operator === true;